import React, { useState, useEffect } from 'react';

/**
 * AnimatedText Component
 * 
 * Displays text with a word-by-word typing animation (AI chatbox style)
 * 
 * @param {string} text - The full text to animate
 * @param {number} speed - Speed in milliseconds between words (default: 50ms)
 * @param {function} onComplete - Callback when animation completes
 * @param {boolean} instant - Skip animation and show full text immediately
 */
const AnimatedText = ({ text, speed = 50, onComplete, instant = false, className = '' }) => {
  const [displayedText, setDisplayedText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  
  useEffect(() => {
    if (instant) {
      setDisplayedText(text);
      if (onComplete) onComplete();
      return;
    }
    
    if (!text) return;
    
    // Split by words while preserving spaces
    const words = text.split(' ');
    
    if (currentIndex < words.length) {
      const timer = setTimeout(() => {
        setDisplayedText(prev => {
          const newText = prev + (prev ? ' ' : '') + words[currentIndex];
          return newText;
        });
        setCurrentIndex(prev => prev + 1);
      }, speed);
      
      return () => clearTimeout(timer);
    } else if (currentIndex === words.length && onComplete) {
      onComplete();
    }
  }, [currentIndex, text, speed, instant]);
  
  // Reset when text changes
  useEffect(() => {
    setDisplayedText('');
    setCurrentIndex(0);
  }, [text]);
  
  return (
    <span className={`animated-text ${className}`}>
      {displayedText}
      {!instant && currentIndex < text?.split(' ')?.length && (
        <span className="smart-suggestion-cursor">|</span>
      )}
    </span>
  );
};

export default AnimatedText;
